import type { StaticPageContent } from '../../shared/content/staticPageContent.js';
import type { LandingEditorElementDefinition } from './landingEditorRegistry.js';

interface GlobusJobEditorRegistrars {
  add: (definition: LandingEditorElementDefinition) => void;
  addContainer: (
    id: string,
    label: string,
    parentId?: string,
    repeat?: { path: string; index: number },
  ) => void;
  addText: (id: string, label: string, path: string, parentId: string, multiline?: boolean) => void;
  addUrl: (id: string, label: string, path: string, parentId: string) => void;
  addImage: (id: string, label: string, path: string, parentId: string) => void;
  addVideo: (id: string, label: string, path: string, parentId: string) => void;
}

export const registerGlobusJobEditorDefinitions = (
  page: StaticPageContent,
  prefix: string,
  { add, addContainer, addText, addUrl, addImage, addVideo }: GlobusJobEditorRegistrars,
) => {
  if (!page.globusJobs?.length || !page.globusJobsOverview) return;
  const jobsId = `${prefix}.globusJobs`;
  addContainer(jobsId, 'GLOBUS Berufsvideos', 'page.root');
  const overviewFields = [
    ['eyebrow', 'GLOBUS Jobs: Eyebrow', false],
    ['title', 'GLOBUS Jobs: Titel', true],
    ['intro', 'GLOBUS Jobs: Intro', true],
    ['videoUnavailable', 'Video: Fehlermeldung', true],
    ['videoLoading', 'Video: Ladetext', false],
    ['sharedBenefitsEyebrow', 'Gemeinsame Vorteile: Eyebrow', false],
    ['sharedBenefitsTitle', 'Gemeinsame Vorteile: Titel', true],
    ['sharedBenefitsText', 'Gemeinsame Vorteile: Text', true],
    ['conditionsTitle', 'Rahmenbedingungen: Titel', false],
    ['conditionsText', 'Rahmenbedingungen: Text', true],
  ] as const;
  overviewFields.forEach(([field, label, multiline]) =>
    addText(`${jobsId}.overview.${field}`, label, `globusJobsOverview.${field}`, jobsId, multiline),
  );
  page.globusJobsOverview.sharedBenefits.forEach((_, index) =>
    addText(
      `${jobsId}.overview.sharedBenefits.${index}`,
      `Gemeinsamer Vorteil ${index + 1}`,
      `globusJobsOverview.sharedBenefits.${index}`,
      jobsId,
      true,
    ),
  );

  page.globusJobs.forEach((job, index) => {
    const jobId = `${jobsId}.${index}`;
    const basePath = `globusJobs.${index}`;
    addContainer(jobId, `GLOBUS Job ${index + 1}: ${job.title}`, jobsId, { path: 'globusJobs', index });
    add({
      id: `${jobId}.profession`,
      label: `GLOBUS Job ${index + 1}: Tätigkeitszuordnung`,
      path: `${basePath}.profession`,
      parentId: jobId,
      kind: 'profession',
    });
    if (job.freshCounterSubtype !== undefined) {
      add({
        id: `${jobId}.freshCounterSubtype`,
        label: `GLOBUS Job ${index + 1}: Frischetheken-Bereich`,
        path: `${basePath}.freshCounterSubtype`,
        parentId: jobId,
        kind: 'freshCounterSubtype',
      });
    }
    addVideo(`${jobId}.video`, `GLOBUS Job ${index + 1}: Video`, `${basePath}.video`, jobId);
    addImage(`${jobId}.poster`, `GLOBUS Job ${index + 1}: Poster`, `${basePath}.poster`, jobId);
    addText(`${jobId}.videoTitle`, `GLOBUS Job ${index + 1}: Videotitel`, `${basePath}.videoTitle`, jobId);
    const captionsId = `${jobId}.captionTracks`;
    addContainer(captionsId, `GLOBUS Job ${index + 1}: Untertitel`, jobId);
    job.captionTracks.forEach((track, trackIndex) => {
      const trackId = `${captionsId}.${trackIndex}`;
      const trackPath = `${basePath}.captionTracks.${trackIndex}`;
      addContainer(trackId, `Untertitel ${track.label}`, captionsId);
      add({
        id: `${trackId}.src`,
        label: `${track.label}: WebVTT-Datei`,
        path: `${trackPath}.src`,
        parentId: trackId,
        kind: 'track',
      });
      addText(`${trackId}.label`, `${track.label}: Auswahltext`, `${trackPath}.label`, trackId);
    });
    addText(`${jobId}.eyebrow`, `GLOBUS Job ${index + 1}: Eyebrow`, `${basePath}.eyebrow`, jobId);
    addText(`${jobId}.title`, `GLOBUS Job ${index + 1}: Titel`, `${basePath}.title`, jobId);
    addText(`${jobId}.introduction`, `GLOBUS Job ${index + 1}: Einstieg`, `${basePath}.introduction`, jobId, true);
    addText(`${jobId}.description`, `GLOBUS Job ${index + 1}: Beschreibung`, `${basePath}.description`, jobId, true);
    if (job.distinction !== undefined) {
      addText(`${jobId}.distinction`, `GLOBUS Job ${index + 1}: Abgrenzung`, `${basePath}.distinction`, jobId, true);
    }
    (['tasks', 'requirements', 'benefits'] as const).forEach(listKey => {
      addText(`${jobId}.${listKey}Heading`, `GLOBUS Job ${index + 1}: ${listKey} Überschrift`, `${basePath}.${listKey}Heading`, jobId);
      job[listKey].forEach((_, itemIndex) =>
        addText(
          `${jobId}.${listKey}.${itemIndex}`,
          `GLOBUS Job ${index + 1}: ${listKey} ${itemIndex + 1}`,
          `${basePath}.${listKey}.${itemIndex}`,
          jobId,
          true,
        ),
      );
    });
    if (job.warning) {
      addText(`${jobId}.warning.title`, `GLOBUS Job ${index + 1}: Warnhinweis Titel`, `${basePath}.warning.title`, jobId);
      addText(`${jobId}.warning.text`, `GLOBUS Job ${index + 1}: Warnhinweis`, `${basePath}.warning.text`, jobId, true);
    }
    addText(`${jobId}.cta.label`, `GLOBUS Job ${index + 1}: CTA-Text`, `${basePath}.cta.label`, jobId);
    addUrl(`${jobId}.cta.href`, `GLOBUS Job ${index + 1}: CTA-Ziel`, `${basePath}.cta.href`, jobId);
  });
};
